import { BoxProps, darken, lighten, useTheme } from "@mui/material";
import { Toolbar } from "common";
import { FakeEvent } from "common/types";
import { useCallback } from "react";

export interface CircleCheckboxGroupProps extends Omit<BoxProps, "onChange"> {
  error?: string;
  label?: string;
  helperText?: string;
  isDirty?: boolean;
  required?: boolean;
  editable?: boolean;
  disabled?: boolean;
  onChange?: (evt: FakeEvent<(number | string)[]>) => void;
  value?: (number | string)[];
  options?: any[];
  multiple?: boolean;
}

export default function CircleCheckboxGroup({
  error,
  options,
  label: _label,
  helperText,
  onChange,
  isDirty,
  editable,
  value = [],
  required,
  disabled,
  sx,
  color,
  multiple = true,
  ...props
}: CircleCheckboxGroupProps) {
  const theme = useTheme();

  const handleChange = useCallback(
    (optionValue: number | string) => {
      let newValue;
      const isChecked = value.includes(optionValue);

      if (multiple) {
        newValue = isChecked
          ? value.filter(v => v !== optionValue)
          : [...value, optionValue];
      } else {
        newValue = isChecked ? [] : [optionValue];
      }

      onChange && onChange({ target: { value: newValue } });
    },
    [onChange, value, multiple]
  );

  return (
    <Toolbar
      sx={{
        gap: 1,
        flex: 1,
        maxWidth: "100%",
        justifyContent: "center",
        ...sx,
      }}
      {...props}
    >
      {options?.map((option: any) => {
        const isChecked = value.includes(option.value);
        const bgColor = option?.color || color || "#000";
        const finalBgColor = isChecked
          ? bgColor
          : theme.palette.mode === "dark"
          ? darken(bgColor, 0.7)
          : lighten(bgColor, 0.7);
        const borderColor =
          theme.palette.mode === "dark"
            ? lighten(bgColor, 0.2)
            : darken(bgColor, 0.2);

        return (
          <div
            title={option?._htmlTitle}
            key={option.value}
            onClick={() => handleChange(option.value)}
            style={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              backgroundColor: finalBgColor,
              color: theme.palette.getContrastText(finalBgColor),
              border: `1px solid ${borderColor}`,
              cursor: disabled || option?.disabled ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.75rem",
              userSelect: "none",
            }}
          >
            {option.label}
          </div>
        );
      })}
    </Toolbar>
  );
}
